import { useEffect, useState } from 'react'
import { useSearchParams, Link as RouterLink } from 'react-router-dom'
import { Mail, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import api from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function VerifyEmail() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const partnerId = params.get('partnerId')

  const [state, setState] = useState('loading')
  const [message, setMessage] = useState('')
  const [resendEmail, setResendEmail] = useState('')
  const [resending, setResending] = useState(false)
  const [resendResult, setResendResult] = useState(null)

  useEffect(() => {
    if (!token || !partnerId) {
      setState('missing')
      setMessage('Este enlace no es válido. Falta información de verificación.')
      return
    }
    (async () => {
      try {
        await api.post('/partners/verify-email', { token, partnerId })
        setState('ok')
        setMessage('Tu email fue verificado correctamente. Ya puedes iniciar sesión.')
      } catch (err) {
        setState('error')
        setMessage(err?.response?.data?.message || 'No se pudo verificar el email')
      }
    })()
  }, [token, partnerId])

  const handleResend = async () => {
    if (!resendEmail) return
    setResending(true)
    setResendResult(null)
    try {
      await api.post('/partners/resend-verification', { email: resendEmail })
      setResendResult({ ok: true, msg: 'Email reenviado. Revisa tu bandeja de entrada.' })
    } catch (err) {
      setResendResult({
        ok: false,
        msg: err?.response?.data?.message || 'Error al reenviar',
      })
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8 pt-safe pb-safe">
      <div className="w-full max-w-md">
        <Card className="p-6 sm:p-8 shadow-md text-center">
          <Mail size={40} className="mx-auto text-primary mb-3" />
          <h1 className="font-display text-h1 font-semibold text-foreground leading-tight">
            Verificación de email
          </h1>

          {state === 'loading' && (
            <div className="my-6 flex flex-col items-center gap-3">
              <Loader2 size={28} className="animate-spin text-primary" />
              <p className="text-body-sm text-subtle">Verificando…</p>
            </div>
          )}

          {state === 'ok' && (
            <div className="mt-4 space-y-4">
              <Alert variant="success" className="text-left">
                <CheckCircle2 size={16} />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <Button asChild size="cta">
                <RouterLink to="/login">Ir al login</RouterLink>
              </Button>
            </div>
          )}

          {(state === 'error' || state === 'missing') && (
            <div className="mt-4 space-y-4 text-left">
              <Alert variant="destructive">
                <AlertCircle size={16} />
                <AlertDescription>{message}</AlertDescription>
              </Alert>

              <p className="text-body-sm text-subtle">
                Si tu token expiró, ingresa el email con el que aplicaste para reenviarlo:
              </p>

              <div className="space-y-1.5">
                <Label htmlFor="ve-email">Email</Label>
                <Input
                  id="ve-email"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                />
              </div>

              <Button
                onClick={handleResend}
                disabled={!resendEmail || resending}
                size="cta"
              >
                {resending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enviando…
                  </>
                ) : (
                  'Reenviar email de verificación'
                )}
              </Button>

              {resendResult && (
                <Alert variant={resendResult.ok ? 'success' : 'destructive'}>
                  <AlertDescription>{resendResult.msg}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
